import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import TablePagination from '../TablePagination';

import * as api from '../../apis';

const SEARCH_TARGET = {
	USER_LOGIN: 'user_login',
	NAME: 'name',
	EMAIL: 'email',
};

const UserSearchModal = ({ onClose, setUser }) => {
	const PAGE_SIZE = 5;

	const [searchTarget, setSearchTarget] = useState(SEARCH_TARGET.NAME);
	const [keyword, setKeyword] = useState('');
	const [userList, setUserList] = useState([]);
	const [pageIndex, setPageIndex] = useState(1);
	const [selectedUser, setSelectedUser] = useState(null);

	useEffect(() => {
		getUsersFromApi();
	}, [pageIndex]);

	const getUsersFromApi = () => {
		let filterParams = [`page=${pageIndex}`, `page_size=${PAGE_SIZE}`];
		if (keyword !== '') filterParams.push(`${searchTarget}=${keyword}`);

		api.getUsers(filterParams)
			.then((res) => {
				if (res && res.data) {
					setUserList(res.data);
				}
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const onClickSearch = () => {
		if (pageIndex === 1) {
			getUsersFromApi();
		} else {
			setPageIndex(1);
		}
	};

	const onPageChange = (page) => {
		setPageIndex(page);
	};

	const onClickConfirm = () => {
		if (!selectedUser) {
			alert('사용자를 선택해주세요.');
			return;
		}
		setUser(selectedUser);
		onClose();
	};

	return (
		<ModalContainer>
			<Content>
				<ModalTopContainer>
					<Title>사용자 검색</Title>
					<CloseButton onClick={onClose}>x</CloseButton>
				</ModalTopContainer>
				<ModalBodyContainer>
					<FilterContents>
						<SearchTargetSelect
							value={searchTarget}
							onChange={(e) => setSearchTarget(e.currentTarget.value)}
						>
							<option value={SEARCH_TARGET.NAME}>이름</option>
							<option value={SEARCH_TARGET.USER_LOGIN}>아이디</option>
							<option value={SEARCH_TARGET.EMAIL}>이메일</option>
						</SearchTargetSelect>
						<SearchKeywordInput
							type="text"
							value={keyword}
							onChange={(e) => setKeyword(e.currentTarget.value)}
							onKeyDown={(event) => {
								if (event.key == 'Enter') {
									onClickSearch();
								}
							}}
							placeholder="검색어를 입력해주세요."
						/>
						<SearchButton onClick={onClickSearch}>검색</SearchButton>
					</FilterContents>
					<SearchResultTable>
						<thead>
							<tr>
								<th style={{ width: '50px' }}></th>
								<th>아이디</th>
								<th>이름</th>
								<th>이메일</th>
								<th>연락처</th>
							</tr>
						</thead>
						<tbody>
							{userList.items && userList.items.length > 0 ? (
								userList.items?.map((user) => (
									<tr key={user.id}>
										<td>
											<input type="radio" name="user" onChange={() => setSelectedUser(user)} />
										</td>
										<td>{user.user_login}</td>
										<td>{user.name}</td>
										<td>{user.email}</td>
										<td>{user.phone}</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan={5}>해당 조건에 일치하는 사용자가 없습니다.</td>
								</tr>
							)}
						</tbody>
					</SearchResultTable>
				</ModalBodyContainer>
				{userList.items && userList.items.length > 0 && (
					<ModalBottomContainer>
						<div className="mt-24px">
							<TablePagination
								currentPage={userList?.pagination.current_page}
								lastPage={userList?.pagination.last_page}
								onPageChange={onPageChange}
							/>
						</div>
						<ConfirmButton onClick={onClickConfirm}>확인</ConfirmButton>
					</ModalBottomContainer>
				)}
			</Content>
		</ModalContainer>
	);
};

const ModalContainer = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(0, 0, 0, 0.7);
	z-index: 99999;
	display: flex;
	justify-content: center;
	align-items: center;

	@media only screen and (max-width: 767.98px) {
		padding: 15px;
	}
`;

const Content = styled.div`
	position: relative;
	width: 720px;
	max-height: 80%;
	overflow-y: scroll;

	background-color: white;
	padding: 40px;

	background: #ffffff;
	box-shadow: 0px 0px 3px 1px rgba(0, 0, 0, 0.25);
	border-radius: 10px;

	@media only screen and (max-width: 767.98px) {
		width: 100%;
		padding: 20px 15px 40px 15px;
	}
`;

const ModalTopContainer = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const Title = styled.span`
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 600;
	font-size: 22px;
	line-height: 26px;
`;

const CloseButton = styled.button`
	display: flex;
	align-items: flex-end;
	justify-content: center;

	width: 30px;
	height: 30px;

	background-color: transparent;
	border: none;

	font-family: 'Noto Sans';
	font-size: 24px;
	font-weight: 500;
	font-style: normal;
	letter-spacing: -1.2px;
	line-height: 30px;
`;

const ModalBodyContainer = styled.div`
	margin-top: 20px;
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const FilterContents = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
`;

const SearchTargetSelect = styled.select`
	width: 100px;
	height: 35px;
	padding-left: 8px;

	border-radius: 4px;
	border: 1px solid #dddddd;

	appearance: none;
	background-image: linear-gradient(45deg, transparent 50%, gray 50%),
		linear-gradient(135deg, gray 50%, transparent 50%);
	background-position: calc(100% - 15px) calc(1em - 2px), calc(100% - 10px) calc(1em - 2px), calc(100% - 2.5em) 0.5em;
	background-size: 5px 5px, 5px 5px, 1px 1.5em;
	background-repeat: no-repeat;

	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 500;
	font-size: 14px;
	line-height: 17px;
	color: #666666;
	cursor: pointer;
`;

const SearchKeywordInput = styled.input`
	width: 100%;
	height: 35px;
	padding: 8px;

	border: 1px solid #dddddd;
	background-color: #ffffff;

	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 500;
	font-size: 14px;
	line-height: 17px;
	color: #666666;
`;

const SearchButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;

	width: 100px;
	height: 35px;
	background-color: #00b1a6;
	border: none;
	border-radius: 5px;
	cursor: pointer;

	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 700;
	font-size: 16px;
	line-height: 19px;
	display: flex;
	align-items: center;
	color: #ffffff;

	&:hover {
		background-color: #008f87;
	}
`;

const SearchResultTable = styled.table`
	width: 100%;

	border-collapse: collapse;
	border-top: 2px solid #0e498f;

	text-align: center;
	margin-top: 15px;
	thead th {
		height: 45px;
		background-color: #f6f8fb;
		border-right: 1px solid #dddddd;

		font-family: 'Pretendard';
		font-style: normal;
		font-weight: 600;
		font-size: 14px;
		line-height: 17px;
		color: #0e498f;

		:first-child {
			border-left: 1px solid #dddddd;
		}
	}
	thead tr {
		border-bottom: 1px solid #dcdcdc;
	}

	tbody td {
		height: 45px;

		border-right: 1px solid #dddddd;
		padding: 6px 2px;

		font-family: 'Pretendard';
		font-style: normal;
		font-weight: 400;
		font-size: 14px;
		line-height: 17px;
		color: #333333;

		word-break: break-all;
		:first-child {
			border-left: 1px solid #dddddd;
		}
	}
	tbody tr {
		border-bottom: 1px solid #dddddd;
	}

	tfoot {
		height: 45px;
		background: #f7f7f7;
		border-bottom: 1px solid #dddddd;
	}
`;

const ModalBottomContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	gap: 24px;
`;

const ConfirmButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100px;
	height: 35px;
	background-color: #2f5a8c;
	border: none;
	border-radius: 5px;
	color: #ffffff;
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 700;
	font-size: 16px;
	line-height: 19px;
	cursor: pointer;
	&:hover {
		background-color: #22426b;
	}
`;

export default UserSearchModal;
